import { useEffect, useId, useMemo, useRef, useState } from 'react';
import type { KeyboardEvent, ReactNode } from 'react';
import { createPortal } from 'react-dom';
import type {
  AdminBannerOption,
  BannerDisplayState,
  BannerOptionStatus,
  BannerRun,
  PlatformKey,
  SaveBannerOptionBody,
} from '@clarity/contract';
import {
  useAdminBannerOptions,
  useCreateBannerOption,
  useRemoveBannerOption,
  useSetBannerOptionStatus,
  useUpdateBannerOption,
} from '../../queries/index.js';
import { useI18n } from '../../lib/i18n.js';
import { dec, num } from '../format.js';
import { BannerOptionRow, type BannerRowOption } from '../campaigns/BannerOptionRow.js';
import { dayMonthYear, runLengthDays, todayUtc } from '../campaigns/bannerFormat.js';

/**
 * Admin "Banner options" page (clarity-banner-options Req 2–4, 9.2, 9.3),
 * reproducing the admin view of the signed-off mock `brand/banner-options-mock.html`.
 *
 * - Head with **Add banner option**.
 * - One list card: platform + state segment filters (`aria-pressed`), an
 *   "N options" count, and expandable rows (name / Arabic / ask-by, platform
 *   chip, where, runs, fee or range, state badge). The expanded body holds the
 *   runs table, the reach line, and Publish / Move to drafts / Edit.
 * - {@link BannerOptionEditor}: a labelled modal (Escape, focus trap, focus
 *   return) with the fields, a runs repeater, field-level errors and the live
 *   "Owners will see" preview rendered by the owner tab's {@link BannerOptionRow}.
 * - A `role="status"` toast after every save / publish / move / delete.
 *
 * The server owns `displayState` and the list order (Req 4.1); this page only
 * filters what it was given. The route guard in `AppRoutes` is the client-side
 * gate; the server enforces admin-only independently (Req 5).
 */

const PLATFORMS: readonly PlatformKey[] = ['talabat', 'careem', 'mythings'];
type PlatformFilter = 'all' | PlatformKey;
type StateFilter = 'all' | BannerDisplayState;
const STATE_FILTERS: readonly StateFilter[] = ['all', 'published', 'draft', 'ended'];

/** How long a toast stays up (mock: 4.2s). */
const TOAST_MS = 4200;

type Translate = ReturnType<typeof useI18n>['t'];

/** The server's `{ body: { message } }` error text from a ts-rest rejection, if any. */
function errorMessage(payload: unknown): string | undefined {
  if (payload && typeof payload === 'object' && 'body' in payload) {
    const body = (payload as { body?: { message?: unknown } }).body;
    if (body && typeof body.message === 'string' && body.message !== '') return body.message;
  }
  return undefined;
}

function platformName(t: Translate, p: PlatformKey): string {
  return t(`bannerOptions.platform.${p}`);
}

/** "45.00" or "45.00–60.00" over the given fees (locale-aware). */
function feeLabel(t: Translate, fees: readonly number[], localeTag: string): string {
  const min = Math.min(...fees);
  const max = Math.max(...fees);
  return min === max
    ? dec(min, 2, localeTag)
    : t('banners.fee.range', { min: dec(min, 2, localeTag), max: dec(max, 2, localeTag) });
}

// ── Page ─────────────────────────────────────────────────────────────────────

/** What the editor is open on: a new option, or an existing one. */
type EditorTarget = { mode: 'add' } | { mode: 'edit'; option: AdminBannerOption };

export function BannerOptionsAdmin() {
  const { t, localeTag } = useI18n();
  const query = useAdminBannerOptions();
  const setStatus = useSetBannerOptionStatus();
  const today = todayUtc();

  const [platform, setPlatform] = useState<PlatformFilter>('all');
  const [state, setState] = useState<StateFilter>('all');
  const [open, setOpen] = useState<ReadonlySet<string>>(() => new Set());
  const [editor, setEditor] = useState<EditorTarget | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [pendingId, setPendingId] = useState<string | null>(null);

  // Toast: one always-mounted live region so every message is announced.
  const [toast, setToast] = useState<string | null>(null);
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  function showToast(message: string) {
    setToast(message);
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), TOAST_MS);
  }
  useEffect(
    () => () => {
      if (toastTimer.current) clearTimeout(toastTimer.current);
    },
    [],
  );

  // Focus return: remember the opener; restore it (or fall back to Add) on close.
  const openerRef = useRef<HTMLElement | null>(null);
  const addRef = useRef<HTMLButtonElement>(null);
  const restoreFocus = useRef(false);
  useEffect(() => {
    if (editor !== null || !restoreFocus.current) return;
    restoreFocus.current = false;
    const opener = openerRef.current;
    openerRef.current = null;
    if (opener && opener.isConnected) opener.focus();
    else addRef.current?.focus();
  }, [editor]);

  function openEditor(target: EditorTarget) {
    openerRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    setEditor(target);
  }
  function closeEditor() {
    restoreFocus.current = true;
    setEditor(null);
  }

  const options: AdminBannerOption[] = useMemo(
    () => (query.data?.status === 200 ? query.data.body.options : []),
    [query.data],
  );
  const filtered = useMemo(
    () =>
      options.filter(
        (o) => (platform === 'all' || o.platformKey === platform) && (state === 'all' || o.displayState === state),
      ),
    [options, platform, state],
  );

  function toggle(id: string) {
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function changeStatus(o: AdminBannerOption, status: BannerOptionStatus) {
    setActionError(null);
    setPendingId(o.id);
    setStatus.mutate(
      { params: { bid: o.id }, body: { status } },
      {
        onSuccess: () => {
          const plat = platformName(t, o.platformKey);
          showToast(
            status === 'published'
              ? t('bannerOptions.toast.publishedShort', { platform: plat, name: o.name })
              : t('bannerOptions.toast.movedToDrafts', { name: o.name }),
          );
        },
        onError: () => setActionError(t('bannerOptions.action.error', { name: o.name })),
        onSettled: () => setPendingId(null),
      },
    );
  }

  const loaded = query.data?.status === 200;

  return (
    <section className="banner-options" aria-labelledby="banner-options-title">
      <div className="page-head banner-options__head">
        <div>
          <h2 id="banner-options-title">{t('bannerOptions.title')}</h2>
          <p className="sub">{t('bannerOptions.sub')}</p>
        </div>
        <button
          ref={addRef}
          type="button"
          className="primary banner-options__add"
          onClick={() => openEditor({ mode: 'add' })}
        >
          <PlusIcon />
          {t('bannerOptions.add')}
        </button>
      </div>

      <div className="card banner-options__card">
        <div className="banner-options__list-head">
          <div className="banner-options__seg" role="group" aria-label={t('bannerOptions.filter.platform.aria')}>
            {(['all', ...PLATFORMS] as const).map((p) => (
              <button key={p} type="button" aria-pressed={platform === p} onClick={() => setPlatform(p)}>
                {p === 'all' ? t('bannerOptions.filter.platform.all') : platformName(t, p)}
              </button>
            ))}
          </div>
          <div className="banner-options__seg" role="group" aria-label={t('bannerOptions.filter.status.aria')}>
            {STATE_FILTERS.map((s) => (
              <button key={s} type="button" aria-pressed={state === s} onClick={() => setState(s)}>
                {t(`bannerOptions.filter.status.${s}`)}
              </button>
            ))}
          </div>
          {loaded && (
            <span className="banner-options__count">
              {t(filtered.length === 1 ? 'bannerOptions.count.one' : 'bannerOptions.count.many', {
                count: num(filtered.length, localeTag),
              })}
            </span>
          )}
        </div>

        {actionError && (
          <div className="banner-options__pad">
            <div className="banner-options__alert" role="alert">
              <AlertIcon />
              <span>{actionError}</span>
            </div>
          </div>
        )}

        {query.isLoading ? (
          <div className="banner-options__loading" aria-busy="true">
            {[60, 80, 50].map((w) => (
              <div key={w} className="sk" style={{ inlineSize: `${w}%` }} />
            ))}
            <span className="sr-only" role="status">
              {t('bannerOptions.loading')}
            </span>
          </div>
        ) : !loaded ? (
          <div className="banner-options__pad">
            <div className="banner-options__alert" role="alert">
              <AlertIcon />
              <span>
                <b>{t('bannerOptions.error.title')}</b> {t('bannerOptions.error.body')}
              </span>
              <button type="button" className="ghost sm" onClick={() => void query.refetch()}>
                {t('bannerOptions.retry')}
              </button>
            </div>
          </div>
        ) : options.length === 0 ? (
          <div className="banner-options__empty">
            <EmptyIcon />
            <h3>{t('bannerOptions.empty.title')}</h3>
            <p>{t('bannerOptions.empty.body')}</p>
            <button type="button" className="primary" onClick={() => openEditor({ mode: 'add' })}>
              <PlusIcon />
              {t('bannerOptions.add')}
            </button>
          </div>
        ) : filtered.length === 0 ? (
          <div className="banner-options__empty" role="status">
            <EmptyIcon />
            <h3>{t('bannerOptions.noMatch.title')}</h3>
            <p>{t('bannerOptions.noMatch.body')}</p>
          </div>
        ) : (
          <ul className="banner-options__list">
            {filtered.map((o) => (
              <AdminRow
                key={o.id}
                option={o}
                open={open.has(o.id)}
                pending={pendingId === o.id}
                onToggle={() => toggle(o.id)}
                onPublish={() => changeStatus(o, 'published')}
                onUnpublish={() => changeStatus(o, 'draft')}
                onEdit={() => openEditor({ mode: 'edit', option: o })}
              />
            ))}
          </ul>
        )}
      </div>

      {editor && (
        <BannerOptionEditor
          option={editor.mode === 'edit' ? editor.option : null}
          defaultPlatform={platform === 'all' ? 'talabat' : platform}
          today={today}
          onClose={closeEditor}
          onDone={(message, openId) => {
            showToast(message);
            if (openId) setOpen((prev) => new Set(prev).add(openId));
            closeEditor();
          }}
        />
      )}

      <div className="banner-options__toast" role="status" aria-live="polite" data-show={toast !== null}>
        {toast !== null && (
          <>
            <CheckIcon />
            <span>{toast}</span>
          </>
        )}
      </div>
    </section>
  );
}

// ── Admin row ────────────────────────────────────────────────────────────────

function AdminRow({
  option: o,
  open,
  pending,
  onToggle,
  onPublish,
  onUnpublish,
  onEdit,
}: {
  option: AdminBannerOption;
  open: boolean;
  pending: boolean;
  onToggle: () => void;
  onPublish: () => void;
  onUnpublish: () => void;
  onEdit: () => void;
}) {
  const { t, localeTag } = useI18n();
  const bodyId = `banner-option-${o.id}`;
  const plat = platformName(t, o.platformKey);
  const runs = o.runs.length;

  return (
    <li className="banner-options__row" data-open={open}>
      <button
        type="button"
        className="banner-options__top"
        aria-expanded={open}
        aria-controls={bodyId}
        onClick={onToggle}
      >
        <span className="banner-options__name-c">
          <span className="banner-options__name">{o.name}</span>
          {o.arabicName && (
            <span className="banner-options__sub">
              <bdi dir="rtl" lang="ar">
                {o.arabicName}
              </bdi>
            </span>
          )}
          <span className="banner-options__sub">
            {o.askBy
              ? t('bannerOptions.row.askBy', { date: dayMonthYear(o.askBy, localeTag) })
              : t('bannerOptions.row.noAskBy')}
          </span>
        </span>
        <span className="banner-options__plat-c">
          <span className={`banner-row__plat banner-row__plat--${o.platformKey}`}>{plat}</span>
        </span>
        <span className="banner-options__where" title={o.where}>
          {o.where}
        </span>
        <span className="banner-options__runs-n">
          {t(runs === 1 ? 'bannerOptions.row.runs.one' : 'bannerOptions.row.runs.many', {
            count: num(runs, localeTag),
          })}
        </span>
        <span className="banner-options__fee">
          {feeLabel(
            t,
            o.runs.map((r) => r.fee),
            localeTag,
          )}{' '}
          {t('banners.fee.currency')}
        </span>
        <span>
          <StateBadge state={o.displayState} />
        </span>
        <ChevIcon />
      </button>

      {open && (
        <div className="banner-options__body" id={bodyId}>
          <table className="banner-options__runs">
            <thead>
              <tr>
                <th scope="col">{t('bannerOptions.runs.col.run')}</th>
                <th scope="col">{t('bannerOptions.runs.col.days')}</th>
                <th scope="col" className="end">
                  {t('bannerOptions.runs.col.fee')}
                </th>
              </tr>
            </thead>
            <tbody>
              {o.runs.map((r) => (
                <tr key={`${r.from}:${r.to}`}>
                  <td className="num">
                    {t('banners.run.dates', {
                      from: dayMonthYear(r.from, localeTag),
                      to: dayMonthYear(r.to, localeTag),
                    })}
                  </td>
                  <td className="num">{num(runLengthDays(r), localeTag)}</td>
                  <td className="num end">{t('banners.run.fee', { fee: dec(r.fee, 2, localeTag) })}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="banner-options__body-foot">
            <span className="banner-options__reach">
              {t(`bannerOptions.reach.${o.displayState}`, { platform: plat })}
            </span>
            {o.displayState === 'draft' && (
              <button type="button" className="primary sm" onClick={onPublish} disabled={pending}>
                {t('bannerOptions.action.publish')}
              </button>
            )}
            {o.displayState === 'published' && (
              <button type="button" className="ghost sm banner-options__quiet" onClick={onUnpublish} disabled={pending}>
                {t('bannerOptions.action.moveToDrafts')}
              </button>
            )}
            <button type="button" className="ghost sm" onClick={onEdit}>
              {t('bannerOptions.action.edit')}
            </button>
          </div>
        </div>
      )}
    </li>
  );
}

function StateBadge({ state }: { state: BannerDisplayState }) {
  const { t } = useI18n();
  return (
    <span className={`banner-options__state banner-options__state--${state}`}>
      <i aria-hidden="true" />
      {t(`bannerOptions.state.${state}`)}
    </span>
  );
}

// ── Editor ───────────────────────────────────────────────────────────────────

/** One run row in the form; `fee` stays a string until submit. */
interface RunDraft {
  key: number;
  from: string;
  to: string;
  fee: string;
}

interface FormDraft {
  platformKey: PlatformKey;
  name: string;
  arabicName: string;
  where: string;
  notes: string;
  askBy: string;
  runs: RunDraft[];
}

/** Which inputs are invalid, and the messages shown under name / where / runs. */
interface FormErrors {
  name?: string | undefined;
  where?: string | undefined;
  runs?: string | undefined;
  /** Invalid run inputs, as `${field}-${runKey}` (e.g. `from-3`). */
  runFields: ReadonlySet<string>;
}

const NO_ERRORS: FormErrors = { runFields: new Set() };

/** A run is previewable when it has both dates in order and a fee above zero. */
function validRun(r: RunDraft): BannerRun | null {
  const fee = Number(r.fee);
  if (!r.from || !r.to || r.to < r.from || r.fee.trim() === '' || !Number.isFinite(fee) || fee <= 0) return null;
  return { from: r.from, to: r.to, fee };
}

/**
 * Client-side mirror of the contract rules (Req 2.1–2.3): name required, ≥ 1
 * run, each run with both dates, `to >= from` and a fee > 0; `where` only when
 * publishing. Returns the errors and the id-suffix of the first invalid input.
 */
function validate(form: FormDraft, forPublish: boolean, t: Translate): { errors: FormErrors; first: string | null } {
  const runFields = new Set<string>();
  const errors: FormErrors = { runFields };
  let first: string | null = null;
  const flag = (field: string) => {
    if (first === null) first = field;
  };

  if (form.name.trim() === '') {
    errors.name = t('bannerOptions.fieldError.name');
    flag('name');
  }
  if (forPublish && form.where.trim() === '') {
    errors.where = t('bannerOptions.fieldError.where');
    flag('where');
  }
  if (form.runs.length === 0) {
    errors.runs = t('bannerOptions.fieldError.runsMin');
    flag('addrun');
  }
  form.runs.forEach((r, i) => {
    const n = i + 1;
    const fail = (field: 'from' | 'to' | 'fee', message: string) => {
      runFields.add(`${field}-${r.key}`);
      errors.runs ??= message;
      flag(`${field}-${r.key}`);
    };
    if (!r.from || !r.to) {
      fail(!r.from ? 'from' : 'to', t('bannerOptions.fieldError.runDates', { n }));
      return;
    }
    if (r.to < r.from) {
      fail('to', t('bannerOptions.fieldError.runOrder', { n }));
      return;
    }
    const fee = Number(r.fee);
    if (r.fee.trim() === '' || !Number.isFinite(fee) || fee <= 0) {
      fail('fee', t('bannerOptions.fieldError.runFee', { n }));
    }
  });
  return { errors, first };
}

/** The wire body (contract rejects empty optional strings, so they become `null`). */
function toBody(form: FormDraft, status: BannerOptionStatus): SaveBannerOptionBody {
  const opt = (s: string) => (s.trim() === '' ? null : s.trim());
  return {
    platformKey: form.platformKey,
    name: form.name.trim(),
    arabicName: opt(form.arabicName),
    where: form.where.trim(),
    notes: opt(form.notes),
    askBy: form.askBy === '' ? null : form.askBy,
    runs: form.runs.map((r) => ({ from: r.from, to: r.to, fee: Number(r.fee) })),
    status,
  };
}

/**
 * The "Owners will see" preview: the draft as a {@link BannerRowOption}, or
 * null when no run is valid yet. Open runs are the valid runs ending on/after
 * today; a fully past-dated draft falls back to all its valid runs so it still
 * previews. The mock always shows the preview as New.
 */
function previewOption(form: FormDraft, today: string): BannerRowOption | null {
  const runs = form.runs
    .map(validRun)
    .filter((r): r is BannerRun => r !== null)
    .sort((a, b) => (a.from < b.from ? -1 : a.from > b.from ? 1 : 0));
  if (runs.length === 0) return null;
  const live = runs.filter((r) => r.to >= today);
  const openRuns = live.length > 0 ? live : runs;
  const nextRun = openRuns[0]!;
  return {
    id: 'preview',
    platformKey: form.platformKey,
    name: form.name.trim(),
    arabicName: form.arabicName.trim() || null,
    where: form.where.trim(),
    notes: form.notes.trim() || null,
    askBy: form.askBy || null,
    runs,
    openRuns,
    nextRun,
    currentRun: openRuns.find((r) => r.from <= today && today <= r.to) ?? null,
    isNew: true,
  };
}

const FOCUSABLE =
  'button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])';

type PendingAction = 'draft' | 'publish' | 'delete' | null;

/**
 * Add / edit a banner option in a labelled modal dialog (Req 2, 3, 4.4, 9.3).
 * Same dialog behaviour as the shared `Modal` (portalled, Escape, backdrop
 * mousedown, Tab focus trap), with its own head / body / footer chrome to
 * match the mock. The caller restores focus to the opener on close.
 */
export function BannerOptionEditor({
  option,
  defaultPlatform,
  today,
  onClose,
  onDone,
}: {
  /** The option being edited, or null to add one. */
  option: AdminBannerOption | null;
  defaultPlatform: PlatformKey;
  today: string;
  onClose: () => void;
  /** A save or delete succeeded: the toast text and, for a save, the id to expand. */
  onDone: (toast: string, openId?: string) => void;
}) {
  const { t } = useI18n();
  const uid = useId();
  const id = (suffix: string) => `${uid}-${suffix}`;
  const dialogRef = useRef<HTMLDivElement>(null);

  const create = useCreateBannerOption();
  const update = useUpdateBannerOption();
  const remove = useRemoveBannerOption();

  const runKey = useRef(0);
  const nextKey = () => ++runKey.current;
  const [form, setForm] = useState<FormDraft>(() =>
    option
      ? {
          platformKey: option.platformKey,
          name: option.name,
          arabicName: option.arabicName ?? '',
          where: option.where,
          notes: option.notes ?? '',
          askBy: option.askBy ?? '',
          runs: option.runs.map((r) => ({ key: nextKey(), from: r.from, to: r.to, fee: String(r.fee) })),
        }
      : {
          platformKey: defaultPlatform,
          name: '',
          arabicName: '',
          where: '',
          notes: '',
          askBy: '',
          runs: [{ key: nextKey(), from: '', to: '', fee: '' }],
        },
  );
  const [errors, setErrors] = useState<FormErrors>(NO_ERRORS);
  const [pending, setPending] = useState<PendingAction>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  /** Field to focus after the next render (first invalid field, or a new run). */
  const [focusField, setFocusField] = useState<string | null>('name');

  const wasPublished = option?.status === 'published';
  const busy = pending !== null;

  useEffect(() => {
    if (focusField === null) return;
    document.getElementById(id(focusField))?.focus();
    setFocusField(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusField]);

  function set<K extends keyof FormDraft>(key: K, value: FormDraft[K]) {
    setForm((f) => ({ ...f, [key]: value }));
    if (key === 'name' || key === 'where') setErrors((e) => ({ ...e, [key]: undefined }));
  }

  function setRun(key: number, field: 'from' | 'to' | 'fee', value: string) {
    setForm((f) => ({ ...f, runs: f.runs.map((r) => (r.key === key ? { ...r, [field]: value } : r)) }));
    setErrors((e) => {
      const tag = `${field}-${key}`;
      if (!e.runFields.has(tag)) return e;
      const runFields = new Set(e.runFields);
      runFields.delete(tag);
      return { ...e, runFields, runs: runFields.size === 0 ? undefined : e.runs };
    });
  }

  function addRun() {
    const key = nextKey();
    setForm((f) => {
      const last = f.runs[f.runs.length - 1];
      return { ...f, runs: [...f.runs, { key, from: '', to: '', fee: last ? last.fee : '' }] };
    });
    setFocusField(`from-${key}`);
  }

  function removeRun(key: number) {
    setForm((f) => (f.runs.length <= 1 ? f : { ...f, runs: f.runs.filter((r) => r.key !== key) }));
    setErrors((e) => {
      const runFields = new Set([...e.runFields].filter((tag) => !tag.endsWith(`-${key}`)));
      return { ...e, runFields, runs: runFields.size === 0 ? undefined : e.runs };
    });
  }

  function failed(err: unknown) {
    setPending(null);
    setSaveError(errorMessage(err) ?? t('bannerOptions.editor.error.body'));
  }

  function save(publish: boolean) {
    if (busy) return;
    const { errors: next, first } = validate(form, publish, t);
    setErrors(next);
    if (first !== null) {
      setFocusField(first);
      return;
    }
    setSaveError(null);
    setPending(publish ? 'publish' : 'draft');
    const status: BannerOptionStatus = publish ? 'published' : 'draft';
    const body = toBody(form, status);
    const plat = platformName(t, body.platformKey);
    const message = publish
      ? wasPublished
        ? t('bannerOptions.toast.saved', { name: body.name })
        : t('bannerOptions.toast.published', { platform: plat, name: body.name })
      : wasPublished
        ? t('bannerOptions.toast.movedToDrafts', { name: body.name })
        : t('bannerOptions.toast.draft', { name: body.name });
    const handlers = {
      onSuccess: (res: { status: number; body: unknown }) => {
        const saved = res.status === 200 ? (res.body as { option: AdminBannerOption }).option : null;
        onDone(message, saved?.id);
      },
      onError: failed,
    };
    if (option) update.mutate({ params: { bid: option.id }, body }, handlers);
    else create.mutate({ body }, handlers);
  }

  function del() {
    if (!option || busy) return;
    setSaveError(null);
    setPending('delete');
    remove.mutate(
      { params: { bid: option.id } },
      {
        onSuccess: () => onDone(t('bannerOptions.toast.deleted', { name: option.name })),
        onError: failed,
      },
    );
  }

  function onKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      onClose();
      return;
    }
    if (e.key !== 'Tab') return;
    const nodes = Array.from(dialogRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE) ?? []);
    const first = nodes[0];
    const last = nodes[nodes.length - 1];
    if (!first || !last) return;
    const active = document.activeElement;
    if (e.shiftKey && (active === first || !dialogRef.current?.contains(active))) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && (active === last || !dialogRef.current?.contains(active))) {
      e.preventDefault();
      first.focus();
    }
  }

  const preview = previewOption(form, today);
  const invalid = (field: string) => errors.runFields.has(field);
  const optional = <span className="banner-editor__hint">{t('bannerOptions.field.optional')}</span>;

  return createPortal(
    <div
      className="modal__backdrop banner-editor__backdrop"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={dialogRef}
        className="banner-editor"
        role="dialog"
        aria-modal="true"
        aria-labelledby={id('title')}
        aria-describedby={id('sub')}
        tabIndex={-1}
        onKeyDown={onKeyDown}
      >
        <div className="banner-editor__head">
          <div>
            <h3 id={id('title')}>
              {option
                ? t('bannerOptions.editor.titleEdit', { name: option.name })
                : t('bannerOptions.editor.titleAdd')}
            </h3>
            <p id={id('sub')}>{t('bannerOptions.editor.sub')}</p>
          </div>
          <button
            type="button"
            className="ghost banner-editor__x"
            onClick={onClose}
            aria-label={t('bannerOptions.editor.close')}
          >
            <XIcon />
          </button>
        </div>

        <form
          className="banner-editor__body"
          noValidate
          onSubmit={(e) => e.preventDefault()}
        >
          {saveError && (
            <div className="banner-options__alert" role="alert">
              <AlertIcon />
              <span>
                <b>{t('bannerOptions.editor.error.title')}</b> {saveError}
              </span>
            </div>
          )}

          <div className="banner-editor__grid2">
            <div className="banner-editor__field">
              <label htmlFor={id('platform')}>{t('bannerOptions.field.platform')}</label>
              <select
                id={id('platform')}
                value={form.platformKey}
                onChange={(e) => set('platformKey', e.target.value as PlatformKey)}
              >
                {PLATFORMS.map((p) => (
                  <option key={p} value={p}>
                    {platformName(t, p)}
                  </option>
                ))}
              </select>
            </div>
            <div className="banner-editor__field">
              <label htmlFor={id('askBy')}>
                {t('bannerOptions.field.askBy')} {optional}
              </label>
              <input
                id={id('askBy')}
                type="date"
                className="num"
                value={form.askBy}
                aria-describedby={id('askBy-hint')}
                onChange={(e) => set('askBy', e.target.value)}
              />
              <span className="banner-editor__hint" id={id('askBy-hint')}>
                {t('bannerOptions.field.askBy.hint')}
              </span>
            </div>
          </div>

          <div className="banner-editor__grid2">
            <div className="banner-editor__field">
              <label htmlFor={id('name')}>{t('bannerOptions.field.name')}</label>
              <input
                id={id('name')}
                value={form.name}
                placeholder={t('bannerOptions.field.name.ph')}
                autoComplete="off"
                aria-invalid={errors.name ? true : undefined}
                aria-describedby={errors.name ? id('e-name') : undefined}
                onChange={(e) => set('name', e.target.value)}
              />
              {errors.name && (
                <span className="banner-editor__err" id={id('e-name')}>
                  {errors.name}
                </span>
              )}
            </div>
            <div className="banner-editor__field">
              <label htmlFor={id('arabicName')}>
                {t('bannerOptions.field.arabicName')} {optional}
              </label>
              <input
                id={id('arabicName')}
                dir="rtl"
                lang="ar"
                value={form.arabicName}
                placeholder={t('bannerOptions.field.arabicName.ph')}
                autoComplete="off"
                onChange={(e) => set('arabicName', e.target.value)}
              />
            </div>
          </div>

          <div className="banner-editor__field">
            <label htmlFor={id('where')}>{t('bannerOptions.field.where')}</label>
            <input
              id={id('where')}
              value={form.where}
              placeholder={t('bannerOptions.field.where.ph')}
              autoComplete="off"
              aria-invalid={errors.where ? true : undefined}
              aria-describedby={errors.where ? id('e-where') : undefined}
              onChange={(e) => set('where', e.target.value)}
            />
            {errors.where && (
              <span className="banner-editor__err" id={id('e-where')}>
                {errors.where}
              </span>
            )}
          </div>

          <div className="banner-editor__field">
            <label htmlFor={id('notes')}>
              {t('bannerOptions.field.notes')} {optional}
            </label>
            <textarea
              id={id('notes')}
              value={form.notes}
              placeholder={t('bannerOptions.field.notes.ph')}
              onChange={(e) => set('notes', e.target.value)}
            />
          </div>

          <div className="banner-editor__field">
            <span className="banner-editor__lbl" id={id('runs-lbl')}>
              {t('bannerOptions.field.runs')}
            </span>
            <div className="banner-editor__runs" role="group" aria-labelledby={id('runs-lbl')}>
              {form.runs.map((r, i) => {
                const describe = (field: string) => (invalid(field) ? id('e-runs') : undefined);
                return (
                  <div className="banner-editor__run" key={r.key}>
                    <div className="banner-editor__field">
                      <label htmlFor={id(`from-${r.key}`)}>{t('bannerOptions.run.from')}</label>
                      <input
                        id={id(`from-${r.key}`)}
                        type="date"
                        className="num"
                        value={r.from}
                        aria-invalid={invalid(`from-${r.key}`) || undefined}
                        aria-describedby={describe(`from-${r.key}`)}
                        onChange={(e) => setRun(r.key, 'from', e.target.value)}
                      />
                    </div>
                    <div className="banner-editor__field">
                      <label htmlFor={id(`to-${r.key}`)}>{t('bannerOptions.run.to')}</label>
                      <input
                        id={id(`to-${r.key}`)}
                        type="date"
                        className="num"
                        value={r.to}
                        aria-invalid={invalid(`to-${r.key}`) || undefined}
                        aria-describedby={describe(`to-${r.key}`)}
                        onChange={(e) => setRun(r.key, 'to', e.target.value)}
                      />
                    </div>
                    <div className="banner-editor__field banner-editor__fee">
                      <label htmlFor={id(`fee-${r.key}`)}>{t('bannerOptions.run.fee')}</label>
                      <input
                        id={id(`fee-${r.key}`)}
                        type="number"
                        min="0"
                        step="0.5"
                        inputMode="decimal"
                        className="num"
                        value={r.fee}
                        placeholder={t('bannerOptions.run.fee.ph')}
                        aria-invalid={invalid(`fee-${r.key}`) || undefined}
                        aria-describedby={describe(`fee-${r.key}`)}
                        onChange={(e) => setRun(r.key, 'fee', e.target.value)}
                      />
                    </div>
                    <button
                      type="button"
                      className="ghost banner-editor__rm"
                      aria-label={t('bannerOptions.run.remove.aria', { n: i + 1 })}
                      disabled={form.runs.length === 1}
                      onClick={() => removeRun(r.key)}
                    >
                      <TrashIcon />
                    </button>
                  </div>
                );
              })}
              <button type="button" id={id('addrun')} className="ghost banner-editor__addrun" onClick={addRun}>
                <PlusIcon />
                {t('bannerOptions.run.add')}
              </button>
            </div>
            {errors.runs && (
              <span className="banner-editor__err" id={id('e-runs')}>
                {errors.runs}
              </span>
            )}
          </div>

          <div className="banner-editor__preview">
            <div className="banner-editor__lbl">{t('bannerOptions.preview.heading')}</div>
            <div className="banner-editor__preview-card">
              {preview ? (
                <ul className="banner-list">
                  <BannerOptionRow option={preview} today={today} showPlatform />
                </ul>
              ) : (
                <p className="banner-editor__preview-empty">{t('bannerOptions.preview.empty')}</p>
              )}
            </div>
          </div>
        </form>

        <div className="banner-editor__foot">
          {option && (
            <button type="button" className="danger" onClick={del} disabled={busy}>
              {pending === 'delete' ? t('bannerOptions.editor.saving') : t('bannerOptions.editor.delete')}
            </button>
          )}
          <span className="banner-editor__sp" />
          <button type="button" className="ghost banner-options__quiet" onClick={onClose}>
            {t('bannerOptions.editor.cancel')}
          </button>
          <button type="button" className="ghost" onClick={() => save(false)} disabled={busy}>
            {pending === 'draft'
              ? t('bannerOptions.editor.saving')
              : wasPublished
                ? t('bannerOptions.editor.moveToDrafts')
                : t('bannerOptions.editor.saveDraft')}
          </button>
          <button type="button" className="primary" onClick={() => save(true)} disabled={busy}>
            {pending === 'publish'
              ? t('bannerOptions.editor.saving')
              : wasPublished
                ? t('bannerOptions.editor.saveChanges')
                : t('bannerOptions.editor.publish')}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

// ── Icons (the mock's stroke set) ──────────────────────────────────────────────

function Icon({ children, className = 'banner-options__i' }: { children: ReactNode; className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {children}
    </svg>
  );
}

function PlusIcon() {
  return (
    <Icon>
      <path d="M12 5v14M5 12h14" />
    </Icon>
  );
}

function ChevIcon() {
  return (
    <Icon className="banner-options__i banner-options__chev">
      <path d="m9 6 6 6-6 6" />
    </Icon>
  );
}

function AlertIcon() {
  return (
    <Icon>
      <path d="M12 4 2.5 20h19z" />
      <path d="M12 10v4M12 17h.01" />
    </Icon>
  );
}

function EmptyIcon() {
  return (
    <Icon className="banner-options__i banner-options__empty-i">
      <rect x="3" y="5" width="18" height="10" rx="2" />
      <path d="M7 19h10M12 15v4" />
      <path d="m9 8 6 4M15 8l-6 4" />
    </Icon>
  );
}

function CheckIcon() {
  return (
    <Icon>
      <path d="m5 12 5 5 9-10" />
    </Icon>
  );
}

function TrashIcon() {
  return (
    <Icon>
      <path d="M4 7h16M9 7V4h6v3M6 7l1 13h10l1-13" />
    </Icon>
  );
}

function XIcon() {
  return (
    <Icon>
      <path d="M6 6l12 12M18 6 6 18" />
    </Icon>
  );
}
